import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminStatsApi } from '../api/stats'
import type {
  StatsDay,
  StatsSummary,
  StatsBreakdownDay,
  RetentionBucket,
  FunnelStep,
  Engagement,
  ChannelRow,
  QuitStats,
} from '../api/stats'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'

const RANGES = [
  { days: 14, label: '최근 14일' },
  { days: 30, label: '최근 30일' },
  { days: 90, label: '최근 90일' },
]

type MetricKey = 'dau' | 'signups' | 'reviews'

const METRICS: Array<{ key: MetricKey; label: string }> = [
  { key: 'dau', label: '앱을 연 사람' },
  { key: 'signups', label: '가입' },
  { key: 'reviews', label: '기록' },
]

const shortDate = (iso: string) => iso.slice(5).replace('-', '.')

/** 3 → "3분", 90 → "2시간", 4316 → "3일" */
const formatStay = (minutes: number): string => {
  if (minutes < 60) return `${minutes}분`
  if (minutes < 60 * 48) return `${Math.round(minutes / 60)}시간`
  return `${Math.round(minutes / 60 / 24)}일`
}

/** 라이브러리 없이 SVG로 그리는 막대그래프 */
const BarChart: React.FC<{ data: StatsDay[]; metric: MetricKey }> = ({
  data,
  metric,
}) => {
  const W = 900
  const H = 220
  const PAD_L = 34
  const PAD_B = 26
  const max = Math.max(1, ...data.map((d) => d[metric]))
  const innerW = W - PAD_L - 8
  const innerH = H - PAD_B - 12
  const barW = Math.max(2, (innerW / Math.max(1, data.length)) * 0.62)
  const step = innerW / Math.max(1, data.length)

  // 눈금은 3줄이면 충분하다. 더 그으면 막대보다 선이 눈에 띈다.
  const ticks = [0, Math.round(max / 2), max]

  // 날짜 라벨이 겹치지 않을 만큼만 띄운다
  const labelEvery = Math.ceil(data.length / 10)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
      {ticks.map((t) => {
        const y = 12 + innerH - (t / max) * innerH
        return (
          <g key={t}>
            <line
              x1={PAD_L}
              y1={y}
              x2={W - 8}
              y2={y}
              stroke="rgba(75,54,33,.10)"
            />
            <text
              x={PAD_L - 6}
              y={y + 4}
              fontSize={11}
              fill="#918063"
              textAnchor="end"
            >
              {t}
            </text>
          </g>
        )
      })}

      {data.map((d, i) => {
        const v = d[metric]
        const h = (v / max) * innerH
        const x = PAD_L + i * step + (step - barW) / 2
        const y = 12 + innerH - h
        const isLast = i === data.length - 1
        return (
          <g key={d.date}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={Math.max(v > 0 ? 1 : 0, h)}
              rx={2}
              /* 마지막 날은 아직 안 끝났으므로 흐리게 */
              fill={isLast ? 'rgba(75,54,33,.35)' : 'rgba(75,54,33,.8)'}
            >
              <title>{`${d.date} · ${v}`}</title>
            </rect>
            {i % labelEvery === 0 && (
              <text
                x={x + barW / 2}
                y={H - 8}
                fontSize={10}
                fill="#918063"
                textAnchor="middle"
              >
                {shortDate(d.date)}
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}

/** 신규·유지·복귀를 쌓아 그리는 막대 */
const StackedChart: React.FC<{ data: StatsBreakdownDay[] }> = ({ data }) => {
  const W = 900
  const H = 200
  const PAD_L = 34
  const PAD_B = 26
  const totals = data.map((d) => d.new + d.retained + d.returning)
  const max = Math.max(1, ...totals)
  const innerW = W - PAD_L - 8
  const innerH = H - PAD_B - 12
  const step = innerW / Math.max(1, data.length)
  const barW = Math.max(2, step * 0.62)
  const labelEvery = Math.ceil(data.length / 10)

  // 아래부터 유지 → 복귀 → 신규 순으로 쌓는다.
  // 매일 있는 층(유지)을 바닥에 두어야 위층의 변화가 눈에 띈다.
  const LAYERS: Array<{ key: 'retained' | 'returning' | 'new'; fill: string }> = [
    { key: 'retained', fill: 'rgba(75,54,33,.8)' },
    { key: 'returning', fill: 'rgba(75,54,33,.45)' },
    { key: 'new', fill: 'rgba(116,157,47,.75)' },
  ]

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
      {[0, Math.round(max / 2), max].map((t) => {
        const y = 12 + innerH - (t / max) * innerH
        return (
          <g key={t}>
            <line x1={PAD_L} y1={y} x2={W - 8} y2={y} stroke="rgba(75,54,33,.10)" />
            <text x={PAD_L - 6} y={y + 4} fontSize={11} fill="#918063" textAnchor="end">
              {t}
            </text>
          </g>
        )
      })}

      {data.map((d, i) => {
        const x = PAD_L + i * step + (step - barW) / 2
        let acc = 0
        return (
          <g key={d.date}>
            {LAYERS.map(({ key, fill }) => {
              const v = d[key]
              const h = (v / max) * innerH
              const y = 12 + innerH - acc - h
              acc += h
              if (v === 0) return null
              return (
                <rect key={key} x={x} y={y} width={barW} height={h} fill={fill}>
                  <title>{`${d.date} · ${key} ${v}`}</title>
                </rect>
              )
            })}
            {i % labelEvery === 0 && (
              <text x={x + barW / 2} y={H - 8} fontSize={10} fill="#918063" textAnchor="middle">
                {shortDate(d.date)}
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}

const Legend: React.FC = () => (
  <div className="flex gap-16px text-style-captionM text-textColorText2 mb-8px">
    {[
      ['rgba(116,157,47,.75)', '신규 — 그날 처음'],
      ['rgba(75,54,33,.8)', '유지 — 최근 7일에도 옴'],
      ['rgba(75,54,33,.45)', '복귀 — 7일 넘게 비었다 돌아옴'],
    ].map(([c, label]) => (
      <span key={label} className="flex items-center gap-6px">
        <i
          className="inline-block w-10px h-10px rounded-2px"
          style={{ background: c }}
        />
        {label}
      </span>
    ))}
  </div>
)

/** 단계별 전환을 가로 막대로 */
const Funnel: React.FC<{ steps: Array<[string, FunnelStep]> }> = ({ steps }) => (
  <div className="flex flex-col gap-10px">
    {steps.map(([label, s]) => (
      <div key={label} className="flex items-center gap-12px">
        <div className="w-[120px] shrink-0 text-style-captionL text-textColorText2">
          {label}
        </div>
        <div className="flex-1 h-20px rounded-4px bg-472410-5% relative overflow-hidden">
          <div
            className="absolute left-0 top-0 bottom-0 rounded-4px"
            style={{ width: `${s.rate}%`, background: 'rgba(75,54,33,.7)' }}
          />
        </div>
        <div className="w-[110px] shrink-0 text-right text-style-captionL text-textColorText2 tabular-nums">
          {s.count.toLocaleString()}명 · {s.rate}%
        </div>
      </div>
    ))}
  </div>
)

const SummaryCard: React.FC<{
  label: string
  value: number | string
  hint?: string
}> = ({ label, value, hint }) => (
  <div className="bg-white rounded-16px shadow-sm p-20px flex-1 min-w-[150px]">
    <div className="text-style-captionL text-textColorText2">{label}</div>
    <div className="font-pretendard font-bold text-28px text-brown-80%-main mt-4px">
      {typeof value === 'number' ? value.toLocaleString() : value}
    </div>
    {hint && (
      <div className="text-style-captionM text-textColorText3 mt-4px">
        {hint}
      </div>
    )}
  </div>
)

/**
 * 사용량 지표.
 *
 * DAU는 '앱을 연 사람' 수다. 로그인 상태로 API를 한 번이라도 부르면 잡히므로
 * 기록을 남긴 사람보다 넓다. 오늘 값은 하루가 끝나야 확정된다.
 */
const AdminStats: React.FC = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [days, setDays] = useState(30)
  const [metric, setMetric] = useState<MetricKey>('dau')
  const [daily, setDaily] = useState<StatsDay[]>([])
  const [breakdown, setBreakdown] = useState<StatsBreakdownDay[]>([])
  const [retention, setRetention] = useState<Record<string, RetentionBucket> | null>(null)
  const [funnel, setFunnel] = useState<Record<string, FunnelStep> | null>(null)
  const [engagement, setEngagement] = useState<Engagement | null>(null)
  const [channels, setChannels] = useState<ChannelRow[]>([])
  const [quit, setQuit] = useState<QuitStats | null>(null)
  const [summary, setSummary] = useState<StatsSummary | null>(null)

  const fetchStats = useCallback(
    async (range: number) => {
      setIsLoading(true)
      try {
        const res = await adminStatsApi.getStats(range)
        setDaily(res.daily)
        setBreakdown(res.breakdown)
        setRetention(res.retention)
        setFunnel(res.funnel)
        setEngagement(res.engagement)
        setChannels(res.channels ?? [])
        setQuit(res.quit ?? null)
        setSummary(res.summary)
      } catch (error: any) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          navigate('/admin/login')
        } else {
          toastUtil.error('지표를 불러오는데 실패했습니다')
        }
      } finally {
        setIsLoading(false)
      }
    },
    [navigate]
  )

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin/login')
      return
    }
    fetchStats(days)
  }, [navigate, fetchStats, days])

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/admin/login')
  }

  // 오늘은 아직 안 끝났으니 평균에서 뺀다
  const average = useMemo(() => {
    const past = daily.slice(0, -1)
    if (past.length === 0) return 0
    return Math.round(
      past.reduce((sum, d) => sum + d[metric], 0) / past.length
    )
  }, [daily, metric])

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={handleLogout}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px">
          <h2 className="text-style-title2 text-textColorText2 mb-8px">지표</h2>
          <p className="text-style-captionL text-textColorText2 mb-24px">
            앱을 연 사람 수입니다. 로그인 상태로 요청이 한 번이라도 오면
            잡히므로 기록을 남긴 사람보다 넓은 값입니다. 오늘 수치는 하루가
            끝나야 확정되므로 그래프에서 흐리게 표시됩니다.
            {summary?.tracking_since && (
              <> 집계 시작일은 {summary.tracking_since.slice(0, 10)}입니다.</>
            )}
          </p>

          {isLoading ? (
            <div className="text-style-body2 text-textColorText2">
              불러오는 중…
            </div>
          ) : (
            <>
              {summary && (
                <div className="flex flex-wrap gap-16px mb-24px max-w-[1100px]">
                  <SummaryCard
                    label="오늘"
                    value={summary.dau_today}
                    hint="아직 진행 중"
                  />
                  <SummaryCard label="주간 (7일)" value={summary.wau} />
                  <SummaryCard label="월간 (30일)" value={summary.mau} />
                  <SummaryCard
                    label="가입자"
                    value={summary.total_users}
                    hint={`활성 ${summary.active_users}명`}
                  />
                  <SummaryCard label="기록" value={summary.total_reviews} />
                  <SummaryCard label="장소" value={summary.total_places} />
                </div>
              )}

              <div className="max-w-[1100px] bg-white rounded-16px shadow-sm p-24px">
                <div className="flex flex-wrap items-center justify-between gap-12px mb-16px">
                  <div className="flex gap-8px">
                    {METRICS.map((m) => (
                      <button
                        key={m.key}
                        onClick={() => setMetric(m.key)}
                        className={`py-8px px-16px rounded-8px text-style-button2 border transition-colors ${
                          metric === m.key
                            ? 'bg-4B3621-80% text-white border-transparent'
                            : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                        }`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>

                  <div className="flex gap-8px">
                    {RANGES.map((r) => (
                      <button
                        key={r.days}
                        onClick={() => setDays(r.days)}
                        className={`py-8px px-16px rounded-8px text-style-button2 border transition-colors ${
                          days === r.days
                            ? 'bg-4B3621-80% text-white border-transparent'
                            : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="text-style-captionL text-textColorText2 mb-8px">
                  하루 평균 <b>{average}</b> (오늘 제외)
                </div>

                <BarChart data={daily} metric={metric} />
              </div>

              {/* 신규·유지·복귀 */}
              <div className="max-w-[1100px] bg-white rounded-16px shadow-sm p-24px mt-24px">
                <h3 className="font-pretendard font-bold text-18px text-textColorText2 mb-4px">
                  누가 들어오나
                </h3>
                <p className="text-style-captionL text-textColorText2 mb-12px">
                  총량만 보면 새로 오는 만큼 나가고 있는지 알 수 없습니다. 신규가
                  줄고 유지가 버티는지, 신규로만 버티는지가 여기서 갈립니다.
                </p>
                <Legend />
                <StackedChart data={breakdown} />
              </div>

              <div className="flex flex-wrap gap-24px mt-24px max-w-[1100px]">
                {/* 재방문 */}
                {retention && (
                  <div className="flex-1 min-w-[320px] bg-white rounded-16px shadow-sm p-24px">
                    <h3 className="font-pretendard font-bold text-18px text-textColorText2 mb-4px">
                      가입하고 다시 오나
                    </h3>
                    <p className="text-style-captionL text-textColorText2 mb-16px">
                      가입 후 N일 안에 한 번이라도 다시 들어온 비율입니다. 가입
                      당일은 세지 않습니다 — 가입하려면 어차피 앱을 열어야 하니까요.
                      기간이 지난 사람만 분모에 넣습니다.
                    </p>
                    {(['d1', 'd7', 'd30'] as const).map((k) => {
                      const b = retention[k]
                      if (!b) return null
                      const label = { d1: '1일 안에', d7: '7일 안에', d30: '30일 안에' }[k]
                      return (
                        <div key={k} className="flex items-center gap-12px mb-10px">
                          <div className="w-[70px] shrink-0 text-style-captionL text-textColorText2">
                            {label}
                          </div>
                          <div className="flex-1 h-20px rounded-4px bg-472410-5% relative overflow-hidden">
                            <div
                              className="absolute left-0 top-0 bottom-0 rounded-4px"
                              style={{ width: `${b.rate}%`, background: 'rgba(75,54,33,.7)' }}
                            />
                          </div>
                          <div className="w-[110px] shrink-0 text-right text-style-captionL text-textColorText2 tabular-nums">
                            {b.returned}/{b.total} · {b.rate}%
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* 활성화 퍼널 */}
                {funnel && (
                  <div className="flex-1 min-w-[320px] bg-white rounded-16px shadow-sm p-24px">
                    <h3 className="font-pretendard font-bold text-18px text-textColorText2 mb-4px">
                      가입하고 쓰기까지
                    </h3>
                    <p className="text-style-captionL text-textColorText2 mb-16px">
                      광고로 사람을 데려올 때 이 전환율이 안 움직이면, 유입이
                      아니라 제품을 손봐야 한다는 뜻입니다.
                    </p>
                    <Funnel
                      steps={[
                        ['가입', funnel.signed_up],
                        ['기록 1개 이상', funnel.first_review],
                        ['기록 3개 이상', funnel.three_reviews],
                        ['장소 저장', funnel.saved_place],
                      ]}
                    />
                  </div>
                )}
              </div>

              {/* 참여·휴면 */}
              {engagement && (
                <div className="max-w-[1100px] bg-white rounded-16px shadow-sm p-24px mt-24px">
                  <h3 className="font-pretendard font-bold text-18px text-textColorText2 mb-16px">
                    얼마나 깊게 쓰나
                  </h3>
                  <div className="flex flex-wrap gap-32px">
                    <div>
                      <div className="text-style-captionL text-textColorText2">
                        주간 활동 중 기록을 남긴 비율
                      </div>
                      <div className="font-pretendard font-bold text-28px text-brown-80%-main mt-4px">
                        {engagement.write_rate}%
                      </div>
                      <div className="text-style-captionM text-textColorText3 mt-2px">
                        {engagement.wau_with_review}명 / {engagement.wau}명
                      </div>
                    </div>
                    <div>
                      <div className="text-style-captionL text-textColorText2">
                        30일 무활동
                      </div>
                      <div className="font-pretendard font-bold text-28px text-brown-80%-main mt-4px">
                        {engagement.dormant_rate}%
                      </div>
                      <div className="text-style-captionM text-textColorText3 mt-2px">
                        활성 계정 중 {engagement.dormant_30d}명
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* 탈퇴 */}
              {quit && (
                <div className="max-w-[1100px] bg-white rounded-16px shadow-sm p-24px mt-24px">
                  <h3 className="font-pretendard font-bold text-18px text-textColorText2 mb-4px">
                    나간 사람
                  </h3>
                  <p className="text-style-captionL text-textColorText2 mb-16px">
                    가입자 수만 보면 늘어나는 것처럼 보이지만, 들어오자마자 나가는
                    사람이 섞여 있으면 그 숫자는 유입이 아닙니다. 얼마나 빨리
                    나갔는지와 나가기 전에 무언가 남겼는지를 함께 봐야 어디서
                    놓쳤는지 알 수 있습니다.
                  </p>

                  <div className="flex flex-wrap gap-16px mb-20px">
                    <SummaryCard
                      label="탈퇴"
                      value={quit.total}
                      hint={`가입자의 ${quit.rate}%`}
                    />
                    <SummaryCard
                      label={`${quit.instant_minutes}분 안에 나감`}
                      value={quit.instant}
                      hint="들어오자마자 이탈"
                    />
                    <SummaryCard
                      label="기록 없이 나감"
                      value={quit.without_review}
                      hint={`탈퇴자 ${quit.total}명 중`}
                    />
                  </div>

                  {quit.recent.length > 0 && (
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-brandPrimary-10%">
                          {['아이디', '탈퇴일', '머문 시간', '기록'].map((h, i) => (
                            <th
                              key={h}
                              className={`py-10px px-12px text-style-labelM text-textColorText2 ${
                                i < 2 ? 'text-left' : 'text-right'
                              }`}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {quit.recent.map((q) => (
                          <tr
                            key={q.username}
                            className="border-b border-brandPrimary-5%"
                          >
                            <td className="py-10px px-12px text-style-body2 text-textColorText2">
                              {q.username}
                            </td>
                            <td className="py-10px px-12px text-style-body2 text-textColorText2">
                              {q.quit_at}
                            </td>
                            <td className="py-10px px-12px text-style-body2 text-textColorText2 text-right tabular-nums">
                              {formatStay(q.stayed_minutes)}
                            </td>
                            <td
                              className={`py-10px px-12px text-style-body2 text-right tabular-nums ${
                                q.reviews === 0
                                  ? 'text-textColorText3'
                                  : 'text-brown-80%-main font-bold'
                              }`}
                            >
                              {q.reviews}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* 유입 경로 */}
              <div className="max-w-[1100px] bg-white rounded-16px shadow-sm p-24px mt-24px">
                <h3 className="font-pretendard font-bold text-18px text-textColorText2 mb-4px">
                  어디서 왔나
                </h3>
                <p className="text-style-captionL text-textColorText2 mb-16px">
                  <b>클릭</b>은 다운로드 페이지에서 스토어로 넘어간 수입니다.
                  <b> 가입</b>부터는 실제로 그 경로로 설치해 계정을 만든 사람이고,
                  기록·잔존은 그중 몇 명이 남았는지입니다. 많이 눌린 경로와
                  사람이 남는 경로는 다를 수 있어서, 두 축을 나란히 봐야
                  어디에 시간을 쓸지 정할 수 있습니다.
                </p>
                <p className="text-style-captionL text-textColorText3 mb-16px">
                  가입 이후 칸은 <b>안드로이드만</b> 셉니다. 애플은 설치 경로를
                  앱에 알려주지 않아 아이폰 사용자는 어느 경로로 왔는지 알 수
                  없습니다. 그래서 클릭은 있는데 가입이 0인 경로라도, iOS 쪽에서는
                  실제로 설치가 있었을 수 있습니다.
                </p>

                {channels.length === 0 ? (
                  <div className="text-style-body2 text-textColorText3">
                    아직 기록이 없습니다. 광고 링크에 utm 값을 붙여 쓰면 여기에
                    쌓입니다.
                  </div>
                ) : (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-brandPrimary-10%">
                        {[
                          '소스',
                          '캠페인',
                          '클릭',
                          'iOS',
                          'Android',
                          '가입',
                          '기록 남김',
                          '최근 7일',
                        ].map((h, i) => (
                          <th
                            key={h}
                            className={`py-10px px-12px text-style-labelM ${
                              i >= 5 ? 'text-textColorText2' : 'text-textColorText2'
                            } ${i < 2 ? 'text-left' : 'text-right'} ${
                              i === 5 ? 'border-l border-brandPrimary-10%' : ''
                            }`}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {channels.map((c) => (
                        <tr
                          key={`${c.source}-${c.campaign}`}
                          className="border-b border-brandPrimary-5%"
                        >
                          <td className="py-10px px-12px text-style-body2 text-textColorText2">
                            {c.source}
                          </td>
                          <td className="py-10px px-12px text-style-body2 text-textColorText2">
                            {c.campaign}
                          </td>
                          <td className="py-10px px-12px text-style-body2 text-brown-80%-main text-right font-bold tabular-nums">
                            {c.clicks.toLocaleString()}
                          </td>
                          <td className="py-10px px-12px text-style-body2 text-textColorText2 text-right tabular-nums">
                            {c.ios}
                          </td>
                          <td className="py-10px px-12px text-style-body2 text-textColorText2 text-right tabular-nums">
                            {c.android}
                          </td>
                          {/* 여기서부터가 '실제로 남은 사람'이다 */}
                          <td className="py-10px px-12px text-style-body2 text-brown-80%-main text-right font-bold tabular-nums border-l border-brandPrimary-10%">
                            {c.signups > 0 ? c.signups.toLocaleString() : '—'}
                          </td>
                          <td className="py-10px px-12px text-style-body2 text-textColorText2 text-right tabular-nums">
                            {c.signups > 0 ? c.with_review : '—'}
                          </td>
                          <td className="py-10px px-12px text-style-body2 text-textColorText2 text-right tabular-nums">
                            {c.signups > 0 ? c.active_7d : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  )
}

export default AdminStats
